/**
 * useVisibilityResume — Detects when the app returns to foreground after
 * being suspended (Safari tab, Android background, PWA resume).
 *
 * On resume: forces Firestore to reconnect immediately instead of waiting
 * for its internal backoff timer, and triggers a callback for UI updates.
 */
import { useEffect, useRef } from "react";
import { db } from "../firebase";
import { enableNetwork, disableNetwork } from "firebase/firestore";

/**
 * @param {Object} options
 * @param {Function} [options.onResume] - Optional callback fired when app resumes
 * @param {number} [options.minHiddenMs=3000] - Min ms hidden before triggering resume logic
 */
export function useVisibilityResume({ onResume, minHiddenMs = 3000 } = {}) {
  const hiddenAtRef = useRef(null);
  const togglingRef = useRef(false);

  useEffect(() => {
    function handleVisibility() {
      if (document.visibilityState === "hidden") {
        hiddenAtRef.current = Date.now();
        return;
      }

      // Visible again — check if we were hidden long enough
      const hiddenAt = hiddenAtRef.current;
      hiddenAtRef.current = null;
      if (!hiddenAt || Date.now() - hiddenAt < minHiddenMs) return;

      // Force Firestore to re-establish its connection. A bare enableNetwork()
      // is a no-op when Firestore still THINKS it's online but the underlying
      // connection died while the PWA was suspended (iOS) — so a write issued
      // right after unlock (e.g. logging an ascent) hangs on the dead channel
      // and silently fails to persist. A full disableNetwork → enableNetwork
      // toggle tears the dead connection down and builds a fresh one, flushing
      // any queued writes. The in-flight guard avoids overlapping toggles if
      // visibility flips again mid-reconnect.
      if (!togglingRef.current) {
        togglingRef.current = true;
        disableNetwork(db)
          .then(() => enableNetwork(db))
          .catch(() => {})
          .finally(() => { togglingRef.current = false; });
      }

      if (onResume) onResume();
    }

    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [onResume, minHiddenMs]);
}
